import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { notificationAPI } from '../../services/api';
import { Logger } from '../../utils/logger';
import { ThemeColors, useTheme } from '../../context/ThemeContext';

interface PreferenceRow {
  category: string;
  inAppEnabled: boolean;
  pushEnabled: boolean;
  emailEnabled: boolean;
  smsEnabled: boolean;
  whatsAppEnabled: boolean;
  isLocked: boolean;
  lockedChannels?: string[];
}

const CHANNELS: { key: keyof PreferenceRow; channel: string; label: string }[] = [
  { key: 'emailEnabled', channel: 'EMAIL', label: 'Email' },
  { key: 'whatsAppEnabled', channel: 'WHATSAPP', label: 'WhatsApp' },
  { key: 'pushEnabled', channel: 'PUSH', label: 'Push' },
  { key: 'inAppEnabled', channel: 'IN_APP', label: 'In-app' },
  { key: 'smsEnabled', channel: 'SMS', label: 'SMS' },
];

const CATEGORY_LABELS: Record<string, string> = {
  GENERAL: 'General announcements',
  ACADEMIC: 'Academics & homework',
  ATTENDANCE: 'Attendance',
  FINANCE: 'Fees & payments',
  TRANSPORT: 'Transport',
  EVENT: 'Events',
  EMERGENCY: 'Emergency',
};

/**
 * The category × channel preference matrix inside Settings — the same matrix
 * the server enforces on every send. EMERGENCY (and any channel the school's
 * policy locks) renders on and untouchable, exactly matching the engine.
 */
export function NotificationPreferencesSection() {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [matrix, setMatrix] = useState<PreferenceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [savedNote, setSavedNote] = useState('');

  useEffect(() => {
    notificationAPI.getPreferences()
      .then(response => setMatrix(response.data ?? []))
      .catch(error => Logger.warn('Preferences', 'Could not load preferences', error))
      .finally(() => setLoading(false));
  }, []);

  const isChannelLocked = (row: PreferenceRow, channel: string) =>
    row.isLocked || (row.lockedChannels ?? []).includes(channel);

  const toggle = (category: string, key: keyof PreferenceRow, channel: string) => {
    setSavedNote('');
    setDirty(true);
    setMatrix(rows => rows.map(row =>
      row.category === category && !isChannelLocked(row, channel)
        ? { ...row, [key]: !row[key] }
        : row));
  };

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const response = await notificationAPI.savePreferences(matrix);
      setMatrix(response.data ?? matrix);
      setDirty(false);
      setSavedNote('Saved — applies to every notification from now on.');
    } catch (error) {
      Logger.warn('Preferences', 'Could not save preferences', error);
      setSavedNote('Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [matrix]);

  if (loading) {
    return <LoadingSpinner size="small" />;
  }

  return (
    <View>
      {matrix.map(row => (
        <View key={row.category} style={styles.categoryBlock}>
          <View style={styles.categoryHeader}>
            <Text style={styles.categoryTitle}>
              {CATEGORY_LABELS[row.category] ?? row.category}
            </Text>
            {row.isLocked && <Text style={styles.lockedTag}>Always on</Text>}
          </View>
          <View style={styles.chipRow}>
            {CHANNELS.map(({ key, channel, label }) => {
              const enabled = Boolean(row[key]);
              const locked = isChannelLocked(row, channel);
              return (
                <TouchableOpacity key={channel}
                  disabled={locked}
                  onPress={() => toggle(row.category, key, channel)}
                  style={[
                    styles.chip,
                    enabled && styles.chipOn,
                    locked && styles.chipLocked,
                  ]}>
                  <Text style={[styles.chipText, enabled && styles.chipTextOn]}>
                    {label}{locked ? ' 🔒' : ''}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      ))}

      <TouchableOpacity style={[styles.saveButton, !dirty && styles.saveButtonIdle]}
        disabled={saving || !dirty} onPress={save}>
        <Text style={styles.saveButtonText}>
          {saving ? 'Saving…' : 'Save preferences'}
        </Text>
      </TouchableOpacity>
      {savedNote !== '' && <Text style={styles.savedNote}>{savedNote}</Text>}
      <Text style={styles.hint}>
        Emergency alerts always arrive on every channel. 🔒 marks channels your
        school has made mandatory.
      </Text>
    </View>
  );
}

function createStyles(colors: ThemeColors) {
  return StyleSheet.create({
  categoryBlock: { marginBottom: 14 },
  categoryHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 },
  categoryTitle: { fontSize: 14, fontWeight: '700', color: colors.textPrimary },
  lockedTag: { fontSize: 11, fontWeight: '700', color: '#DC2626', backgroundColor: '#FEF2F2', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8, overflow: 'hidden' },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  chip: { borderRadius: 16, paddingVertical: 6, paddingHorizontal: 12, backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border },
  chipOn: { backgroundColor: colors.primaryLight, borderColor: colors.primary },
  chipLocked: { opacity: 0.65 },
  chipText: { fontSize: 12, fontWeight: '600', color: colors.textSecondary },
  chipTextOn: { color: colors.primary },
  saveButton: { backgroundColor: colors.primary, borderRadius: 10, paddingVertical: 10, alignItems: 'center', marginTop: 4 },
  saveButtonIdle: { opacity: 0.5 },
  saveButtonText: { color: colors.textInverse, fontWeight: '700', fontSize: 14 },
  savedNote: { fontSize: 12, color: '#16A34A', marginTop: 6, textAlign: 'center' },
  hint: { fontSize: 11, color: colors.textMuted, marginTop: 8, lineHeight: 16 },
  });
}
